// Global Instructions Rule Applied!
//
// Configuration → Contact Relationships: the types → titles hierarchy over
// cfg.contact_relationship_types / cfg.contact_relationship_titles.
// Master–detail chrome copied from LookupListsPanel (types table → one type's
// titles); in-use handling copied from PipelineStatusesPanel / IndustriesPanel
// (usage counts, delete-in-use routes into the Replace flow).
//
// Rows whose ids are hardcoded in application logic (globals.ts /
// globals.py; 'Founder' is also label-matched) are system-protected: no
// rename, no delete — the backend refuses them regardless. A protected
// type's comms/tracking flags stay editable.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SwapOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  RELATIONSHIP_TYPE_FOUNDER,
  RELATIONSHIP_TYPE_EMPLOYEE,
  RELATIONSHIP_TYPE_ADVISOR,
  RELATIONSHIP_TYPE_OTHER,
  RELATIONSHIP_CATEGORY_FOUNDER,
  RELATIONSHIP_CATEGORY_INTRODUCED_BY,
  RELATIONSHIP_CATEGORY_SOURCED_BY,
  RELATIONSHIP_CATEGORY_LEAD
} from '../../../state_manager/globals.ts'
import {
  type ContactRelationshipTypeAdminRow,
  type ContactRelationshipTitleAdminRow
} from '../../../state_manager/data_models/contact_relationships_config_model.ts'
import {
  TitleInUseError,
  createContactRelationshipTitle,
  createContactRelationshipType,
  deleteContactRelationshipTitle,
  deleteContactRelationshipType,
  fetchContactRelationshipTitles,
  fetchContactRelationshipTypes,
  reassignContactRelationshipTitles,
  updateContactRelationshipTitle,
  updateContactRelationshipType
} from '../services/contactRelationshipsConfigService.ts'
import { ReplaceContactRelationshipTitleModal } from './ReplaceContactRelationshipTitleModal.tsx'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'

const { Title, Text } = Typography

// Mirror of the backend deny-list (PROTECTED_CONTACT_RELATIONSHIP_*_IDS in
// contact_relationship_params.py): shown read-only here; the backend refuses
// their renames/deletes regardless.
export const PROTECTED_TYPE_IDS = new Set<string>([
  RELATIONSHIP_TYPE_FOUNDER,
  RELATIONSHIP_TYPE_EMPLOYEE,
  RELATIONSHIP_TYPE_ADVISOR,
  RELATIONSHIP_TYPE_OTHER
])
export const PROTECTED_TITLE_IDS = new Set<string>([
  RELATIONSHIP_CATEGORY_FOUNDER,
  RELATIONSHIP_CATEGORY_INTRODUCED_BY,
  RELATIONSHIP_CATEGORY_SOURCED_BY,
  RELATIONSHIP_CATEGORY_LEAD
])

export default function ContactRelationshipsPanel() {
  const [types, setTypes] = useState<ContactRelationshipTypeAdminRow[]>([])
  const [loading, setLoading] = useState(false)

  // Master–detail: when set, show that type's titles.
  const [detailType, setDetailType] = useState<ContactRelationshipTypeAdminRow | null>(null)
  const [titles, setTitles] = useState<ContactRelationshipTitleAdminRow[]>([])
  const [titlesLoading, setTitlesLoading] = useState(false)

  // Add / rename modal (shared by types and titles — scope decided by view).
  const [modalOpen, setModalOpen] = useState(false)
  const [editingType, setEditingType] = useState<ContactRelationshipTypeAdminRow | null>(null)
  const [editingTitle, setEditingTitle] = useState<ContactRelationshipTitleAdminRow | null>(null)
  const [nameValue, setNameValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [savingId, setSavingId] = useState<string | null>(null)

  // Replace flow
  const [replaceOpen, setReplaceOpen] = useState(false)
  const [replaceSourceIds, setReplaceSourceIds] = useState<string[] | undefined>(undefined)
  const [replaceLoading, setReplaceLoading] = useState(false)

  const token = () => getAccessToken(store.getState().auth)

  const loadTypes = useCallback(async () => {
    setLoading(true)
    try {
      setTypes(await fetchContactRelationshipTypes(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load relationship types')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTypes()
  }, [loadTypes])

  const loadTitles = useCallback(async (type: ContactRelationshipTypeAdminRow) => {
    setTitlesLoading(true)
    try {
      setTitles(await fetchContactRelationshipTitles(type.contact_relationship_types__id, token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load titles')
    } finally {
      setTitlesLoading(false)
    }
  }, [])

  const openType = (type: ContactRelationshipTypeAdminRow) => {
    setDetailType(type)
    loadTitles(type)
  }

  const refreshAfterWrite = async () => {
    if (detailType) await loadTitles(detailType)
    await loadTypes()
    refreshBoardConfigs()
  }

  const submitName = async () => {
    const name = nameValue.trim()
    if (!name) return
    setSubmitting(true)
    try {
      if (detailType) {
        if (editingTitle) {
          await updateContactRelationshipTitle(
            editingTitle.contact_relationship_titles__id, { name }, token())
          toast.success('Title renamed')
        } else {
          await createContactRelationshipTitle(
            { name, type_id: detailType.contact_relationship_types__id }, token())
          toast.success('Title added')
        }
      } else if (editingType) {
        await updateContactRelationshipType(
          editingType.contact_relationship_types__id, { name }, token())
        toast.success('Type renamed')
      } else {
        await createContactRelationshipType({ name }, token())
        toast.success('Type added')
      }
      setModalOpen(false)
      await refreshAfterWrite()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const toggleTypeFlag = async (
    row: ContactRelationshipTypeAdminRow,
    flag: 'gets_comms' | 'track_interactions',
    checked: boolean
  ) => {
    setSavingId(row.contact_relationship_types__id)
    try {
      await updateContactRelationshipType(
        row.contact_relationship_types__id, { [flag]: checked }, token())
      await refreshAfterWrite()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Update failed')
      await loadTypes() // snap the switch back
    } finally {
      setSavingId(null)
    }
  }

  const handleDeleteType = async (row: ContactRelationshipTypeAdminRow) => {
    try {
      await deleteContactRelationshipType(row.contact_relationship_types__id, token())
      toast.success('Type deleted')
      await refreshAfterWrite()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  const handleDeleteTitle = async (row: ContactRelationshipTitleAdminRow) => {
    // Client-side pre-check (Industries pattern): an in-use delete opens the
    // Replace flow directly, without a doomed round trip.
    if ((row.contact_relationship_titles__usage_count || 0) > 0) {
      setReplaceSourceIds([row.contact_relationship_titles__id])
      setReplaceOpen(true)
      return
    }
    try {
      await deleteContactRelationshipTitle(row.contact_relationship_titles__id, token())
      toast.success('Title deleted')
      await refreshAfterWrite()
    } catch (e) {
      if (e instanceof TitleInUseError) {
        setReplaceSourceIds([row.contact_relationship_titles__id])
        setReplaceOpen(true)
        return
      }
      showErrorToast(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  const handleReassign = async (sourceIds: string[], targetId: string, deleteSources: boolean) => {
    setReplaceLoading(true)
    try {
      await reassignContactRelationshipTitles(sourceIds, targetId, deleteSources, token())
      toast.success('References reassigned')
      setReplaceOpen(false)
      setReplaceSourceIds(undefined)
      await refreshAfterWrite()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Reassign failed')
    } finally {
      setReplaceLoading(false)
    }
  }

  const usageTag = (count: number, tooltip: string) =>
    count > 0 ? (
      <Tooltip title={tooltip}>
        <Tag>{count} reference{count === 1 ? '' : 's'}</Tag>
      </Tooltip>
    ) : (
      <Text type='secondary'>unused</Text>
    )

  const typeColumns: ColumnsType<ContactRelationshipTypeAdminRow> = [
    {
      title: 'Type',
      dataIndex: 'contact_relationship_types__name',
      key: 'name',
      sorter: (a, b) =>
        a.contact_relationship_types__name.localeCompare(b.contact_relationship_types__name),
      render: (name: string, row) => (
        <Space size='small'>
          <Button type='link' style={{ padding: 0 }} onClick={() => openType(row)}>
            {name}
          </Button>
          {PROTECTED_TYPE_IDS.has(row.contact_relationship_types__id) && (
            <Tooltip title='Referenced by id in application logic — cannot be renamed or deleted'>
              <Tag>system</Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Titles',
      dataIndex: 'contact_relationship_types__titles_count',
      key: 'titles',
      width: 90,
      align: 'right',
      sorter: (a, b) =>
        a.contact_relationship_types__titles_count - b.contact_relationship_types__titles_count,
      render: (count: number) => <Text type='secondary'>{count}</Text>
    },
    {
      title: 'In use',
      dataIndex: 'contact_relationship_types__usage_count',
      key: 'usage',
      width: 130,
      sorter: (a, b) =>
        a.contact_relationship_types__usage_count - b.contact_relationship_types__usage_count,
      render: (count: number) =>
        usageTag(count, 'Company, deal and prospect contacts related through this type’s titles')
    },
    {
      title: 'Comms',
      key: 'gets_comms',
      width: 90,
      render: (_, row) => (
        <Tooltip title='Contacts related through this type receive distribution-list comms'>
          <Switch
            size='small'
            checked={row.contact_relationship_types__gets_comms}
            loading={savingId === row.contact_relationship_types__id}
            onChange={(checked) => toggleTypeFlag(row, 'gets_comms', checked)}
          />
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, row) => {
        const isProtected = PROTECTED_TYPE_IDS.has(row.contact_relationship_types__id)
        if (isProtected) return null
        const blocked =
          row.contact_relationship_types__titles_count > 0 ||
          row.contact_relationship_types__usage_count > 0
        return (
          <Space size={0}>
            <Tooltip title='Rename'>
              <Button
                type='text' size='small' icon={<EditOutlined />}
                onClick={() => {
                  setEditingType(row)
                  setEditingTitle(null)
                  setNameValue(row.contact_relationship_types__name)
                  setModalOpen(true)
                }}
              />
            </Tooltip>
            <Popconfirm
              title={
                blocked
                  ? 'This type still has titles — delete or replace them first.'
                  : 'Delete this relationship type?'
              }
              okText='Delete'
              okButtonProps={{ danger: true, disabled: blocked }}
              onConfirm={() => handleDeleteType(row)}
            >
              <Button type='text' size='small' danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    }
  ]

  const titleColumns: ColumnsType<ContactRelationshipTitleAdminRow> = [
    {
      title: 'Title',
      dataIndex: 'contact_relationship_titles__name',
      key: 'name',
      sorter: (a, b) =>
        a.contact_relationship_titles__name.localeCompare(b.contact_relationship_titles__name),
      defaultSortOrder: 'ascend',
      render: (name: string, row) => (
        <Space size='small'>
          <Text>{name}</Text>
          {PROTECTED_TITLE_IDS.has(row.contact_relationship_titles__id) && (
            <Tooltip title='Referenced by id in application logic — cannot be renamed or deleted'>
              <Tag>system</Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'In use',
      dataIndex: 'contact_relationship_titles__usage_count',
      key: 'usage',
      width: 140,
      sorter: (a, b) =>
        a.contact_relationship_titles__usage_count - b.contact_relationship_titles__usage_count,
      render: (count: number, row) =>
        usageTag(
          count,
          `${row.contact_relationship_titles__accounts_count} company / ` +
            `${row.contact_relationship_titles__deals_count} deal / ` +
            `${row.contact_relationship_titles__prospects_count} prospect contact(s)`
        )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, row) => {
        const isProtected = PROTECTED_TITLE_IDS.has(row.contact_relationship_titles__id)
        if (isProtected) return null
        const inUse = (row.contact_relationship_titles__usage_count || 0) > 0
        return (
          <Space size={0}>
            <Tooltip title='Rename'>
              <Button
                type='text' size='small' icon={<EditOutlined />}
                onClick={() => {
                  setEditingTitle(row)
                  setEditingType(null)
                  setNameValue(row.contact_relationship_titles__name)
                  setModalOpen(true)
                }}
              />
            </Tooltip>
            <Popconfirm
              title={
                inUse
                  ? 'This title is in use — deleting opens the Replace flow.'
                  : 'Delete this title?'
              }
              okText={inUse ? 'Replace…' : 'Delete'}
              okButtonProps={{ danger: !inUse }}
              onConfirm={() => handleDeleteTitle(row)}
            >
              <Button type='text' size='small' danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    }
  ]

  const nameModal = (
    <Modal
      title={
        detailType
          ? editingTitle ? 'Rename title' : 'New title'
          : editingType ? 'Rename type' : 'New type'
      }
      open={modalOpen}
      okText={editingTitle || editingType ? 'Rename' : 'Add'}
      okButtonProps={{ disabled: !nameValue.trim() }}
      confirmLoading={submitting}
      onOk={submitName}
      onCancel={() => setModalOpen(false)}
      destroyOnClose
    >
      <Input
        value={nameValue}
        onChange={(e) => setNameValue(e.target.value)}
        onPressEnter={submitName}
        placeholder='Name'
        autoFocus
      />
    </Modal>
  )

  if (detailType) {
    return (
      <div className='flex flex-col h-full min-h-0'>
        <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                setDetailType(null)
                setTitles([])
              }}
            >
              All types
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {detailType.contact_relationship_types__name}
            </Title>
          </Space>
          <Space>
            <Button
              icon={<SwapOutlined />}
              disabled={titles.length < 2}
              onClick={() => {
                setReplaceSourceIds(undefined)
                setReplaceOpen(true)
              }}
            >
              Replace / Merge
            </Button>
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTitle(null)
                setEditingType(null)
                setNameValue('')
                setModalOpen(true)
              }}
            >
              New Title
            </Button>
          </Space>
        </div>

        <Text type='secondary' style={{ marginBottom: 12 }}>
          Titles a contact can hold under the “{detailType.contact_relationship_types__name}”
          relationship type. A title belongs to exactly one type; deleting an in-use title
          reassigns its contacts to a replacement first.
        </Text>

        <div className='flex-1 min-h-0 overflow-auto'>
          <Table
            size='small'
            rowKey='contact_relationship_titles__id'
            columns={titleColumns}
            dataSource={titles}
            loading={titlesLoading}
            pagination={false}
          />
        </div>

        {nameModal}

        <ReplaceContactRelationshipTitleModal
          open={replaceOpen}
          titles={titles}
          protectedTitleIds={PROTECTED_TITLE_IDS}
          initialSourceIds={replaceSourceIds}
          confirmLoading={replaceLoading}
          onConfirm={handleReassign}
          onCancel={() => {
            setReplaceOpen(false)
            setReplaceSourceIds(undefined)
          }}
        />
      </div>
    )
  }

  return (
    <div className='flex flex-col h-full min-h-0'>
      <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
        <Title level={4} style={{ margin: 0 }}>
          Contact Relationships
        </Title>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingType(null)
            setEditingTitle(null)
            setNameValue('')
            setModalOpen(true)
          }}
        >
          New Type
        </Button>
      </div>
      <Text type='secondary' style={{ marginBottom: 12 }}>
        Relationship types and the titles beneath them govern how contacts relate to
        companies, deals and prospects. Click a type to manage its titles; rows marked
        “system” are referenced by application logic and cannot be renamed or deleted.
      </Text>
      <div className='flex-1 min-h-0 overflow-auto'>
        <Table
          size='small'
          rowKey='contact_relationship_types__id'
          columns={typeColumns}
          dataSource={types}
          loading={loading}
          pagination={false}
        />
      </div>
      {nameModal}
    </div>
  )
}
